<template>
  <div class="step-confirm">
    <h2 class="step-confirm__title">Confirme o seu agendamento</h2>

    <div class="step-confirm__summary card mb-4">
      <h4 class="mb-3">Resumo</h4>
      <div class="step-confirm__row">
        <span class="step-confirm__label"
          ><i class="pi pi-briefcase mr-2" />Serviço</span
        >
        <span class="step-confirm__value">{{ service.name }}</span>
      </div>
      <div class="step-confirm__row">
        <span class="step-confirm__label"
          ><i class="pi pi-calendar mr-2" />Data</span
        >
        <span class="step-confirm__value">{{ formattedDate }}</span>
      </div>
      <div class="step-confirm__row">
        <span class="step-confirm__label"
          ><i class="pi pi-clock mr-2" />Hora</span
        >
        <span class="step-confirm__value">{{ formattedTime }}</span>
      </div>
      <div class="step-confirm__row">
        <span class="step-confirm__label"
          ><i class="pi pi-video mr-2" />Tipo</span
        >
        <span class="step-confirm__value">{{ meetingTypeLabel }}</span>
      </div>
      <div v-if="displayPrice" class="step-confirm__row">
        <span class="step-confirm__label"
          ><i class="pi pi-euro mr-2" />Preço</span
        >
        <span class="step-confirm__value">{{ displayPrice }}</span>
      </div>
      <div v-if="displayOldPriceAndDiscount" class="step-confirm__row">
        <span class="step-confirm__label"
          ><i class="pi pi-tag mr-2" />Promoção</span
        >
        <span class="step-confirm__value"
          >-{{ promotionDiscountLabel }} (antes: {{ oldPriceDisplay }})</span
        >
      </div>
    </div>

    <div class="step-confirm__client card mb-4">
      <h4 class="mb-3">Os seus dados</h4>
      <div class="step-confirm__row">
        <span class="step-confirm__label"
          ><i class="pi pi-user mr-2" />Nome</span
        >
        <span class="step-confirm__value">{{
          clientName ? clientName : $root.user.name
        }}</span>
      </div>
    </div>

    <div class="step-confirm__notes mb-4">
      <label for="appt-notes" class="step-confirm__field-label"
        >Observações para o consultor (opcional)</label
      >
      <Textarea
        id="appt-notes"
        v-model="notes"
        :maxlength="1000"
        rows="3"
        class="w-full mt-1"
        placeholder="Descreva o seu caso, dúvidas ou qualquer informação relevante..."
        @input="emitValue"
      />
      <small class="step-confirm__char-count">{{ notes.length }}/1000</small>
    </div>

    <div class="step-confirm__payment mb-4">
      <label class="step-confirm__field-label">Método de pagamento</label>
      <div class="step-confirm__payment-options mt-2">
        <div
          v-for="option in paymentOptions"
          :key="option.value"
          class="payment-option"
          :class="{
            'payment-option--selected': paymentMethod === option.value,
          }"
          @click="selectPayment(option.value)"
        >
          <i :class="option.icon" />
          <span>{{ option.label }}</span>
        </div>
      </div>
      <p
        v-if="advancePaymentRequired"
        class="step-confirm__payment-hint mt-2 mb-0"
      >
        <i class="pi pi-info-circle mr-1" />
        Este serviço exige pagamento antecipado. Recomendamos
        <strong>pagar online (Stripe)</strong> para concluir a reserva.
      </p>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";

export default {
  name: "AppointmentStepConfirm",
  props: {
    service: { type: Object, required: true },
    dateTime: { type: Object, required: true }, // { date, time, startDateUTC }
    /** 'online' | 'on-site' escolhido ou único do serviço */
    appointmentMode: { type: String, default: null },
    clientName: { type: String, required: false },
  },
  emits: ["update:notes", "update:paymentMethod", "valid"],
  data() {
    return {
      notes: "",
      paymentMethod: null,
      paymentDefaultsApplied: false,
    };
  },
  computed: {
    formattedDate() {
      if (!this.dateTime || !this.dateTime.date) return "";
      return dayjs(this.dateTime.date).format("DD/MM/YYYY");
    },
    formattedTime() {
      if (!this.dateTime || !this.dateTime.time) return "";
      return this.dateTime.time;
    },
    meetingTypeLabel() {
      const mode = this.appointmentMode || this.service.meetingType;
      const map = {
        online: "Online",
        "on-site": "Presencial",
        both: "Online ou Presencial",
      };
      return map[mode] || "";
    },
    resolvedMode() {
      return this.appointmentMode || this.service.meetingType;
    },
    advancePaymentRequired() {
      const mode = this.resolvedMode;
      if (mode === "online") return !!this.service.hasOnlineAdvancePayment;
      if (mode === "on-site") return !!this.service.hasOnSiteAdvancePayment;
      return !!(
        this.service.hasOnlineAdvancePayment ||
        this.service.hasOnSiteAdvancePayment
      );
    },
    /** Mostrar opção Stripe: antecipado online/presencial ou URL legada no serviço */
    showStripePaymentOption() {
      const mode = this.resolvedMode;
      if (mode === "online" && this.service.hasOnlineAdvancePayment)
        return true;
      if (mode === "on-site" && this.service.hasOnSiteAdvancePayment)
        return true;
      return !!this.service.paymentUrl;
    },
    displayPrice() {
      const raw =
        this.service.priceToShow !== undefined &&
        this.service.priceToShow !== null
          ? this.service.priceToShow
          : this.service.priceWithIva !== undefined &&
            this.service.priceWithIva !== null
          ? this.service.priceWithIva
          : this.service.price;
      if (raw === null || raw === undefined || raw === "") return null;
      const amount = parseFloat(raw);
      if (isNaN(amount)) return null;
      const symbol =
        this.service.currency ||
        this.currencyFromCountry(this.service.country) ||
        "€";
      const suffix =
        this.service.pricingSource === "card"
          ? "Desconto Balcão Mais"
          : this.service.pricingSource === "promotion"
          ? "Promoção"
          : "IVA incl.";
      return `${symbol} ${amount.toFixed(2).replace(".", ",")} (${suffix})`;
    },
    paymentOptions() {
      const options = [
        {
          value: "bank_transfer",
          label: "Transferência Bancária",
          icon: "pi pi-building",
        },
      ];
      // Consulta online não tem balcão (INTRA-2199)
      if (this.resolvedMode !== "online") {
        options.push({
          value: "office",
          label: "Pagamento ao Balcão",
          icon: "pi pi-map-marker",
        });
      }
      if (this.showStripePaymentOption) {
        options.push({
          value: "stripe",
          label: "Pagar online (Stripe)",
          icon: "pi pi-credit-card",
        });
      }
      return options;
    },
    oldPriceDisplay() {
      const raw = this.service.oldPriceToShow;
      if (raw === null || raw === undefined || raw === "") return null;
      const amount = parseFloat(raw);
      if (isNaN(amount)) return null;
      const symbol =
        this.service.currency ||
        this.currencyFromCountry(this.service.country) ||
        "€";
      return `${symbol} ${amount.toFixed(2).replace(".", ",")}`;
    },
    promotionDiscountLabel() {
      const d = parseFloat(this.service.discountPercent);
      if (isNaN(d)) return "";
      if (Math.round(d) === d) return String(d) + "%";
      return d.toFixed(2).replace(".", ",") + "%";
    },
    displayOldPriceAndDiscount() {
      return (
        !!this.service.pricingSource &&
        !!this.oldPriceDisplay &&
        !!this.promotionDiscountLabel
      );
    },
  },
  watch: {
    paymentOptions: {
      immediate: true,
      handler(options) {
        if (this.paymentMethod) {
          var allowed = options.some((o) => o.value === this.paymentMethod);
          if (!allowed) {
            this.paymentMethod = null;
            this.paymentDefaultsApplied = false;
          } else {
            this.validateStep();
            return;
          }
        }
        if (this.paymentDefaultsApplied) return;
        if (!options.length) return;
        var prefersStripe =
          options.some((o) => o.value === "stripe") &&
          this.advancePaymentRequired;
        var pick = prefersStripe ? "stripe" : options[0].value;
        this.paymentMethod = pick;
        this.paymentDefaultsApplied = true;
        this.$emit("update:paymentMethod", pick);
        this.validateStep();
      },
    },
    appointmentMode() {
      this.paymentDefaultsApplied = false;
      this.paymentMethod = null;
      this.$emit("update:paymentMethod", null);
    },
    service: {
      deep: true,
      handler() {
        this.paymentDefaultsApplied = false;
      },
    },
  },
  methods: {
    currencyFromCountry(country) {
      const map = {
        PT: "EUR",
        BR: "BRL",
        AO: "AOA",
        MZ: "MZN",
        GW: "XOF",
        CV: "CVE",
        ST: "STN",
        TL: "USD",
      };
      return map[String(country || "").toUpperCase()] || null;
    },
    selectPayment(value) {
      this.paymentMethod = value;
      this.$emit("update:paymentMethod", value);
      this.validateStep();
    },
    emitValue() {
      this.$emit("update:notes", this.notes);
    },
    validateStep() {
      this.$emit("valid", !!this.paymentMethod);
    },
  },
};
</script>
